import React, { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon, Trash2, Eye, Mail, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { DealerLookupsEditor } from "@/components/DealerLookupsEditor";

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState("");
  const [agency, setAgency] = useState("");
  const [phone, setPhone] = useState("");
  const [digest, setDigest] = useState({ enabled: true, flagged_dealers: true, tasks: true, appointments: true, only_critical: false });
  const [digestSaving, setDigestSaving] = useState(false);
  const saveDigest = async (next) => {
    setDigest(next); setDigestSaving(true);
    try { await api.put("/auth/profile", { digest_prefs: next }); await refreshMe(); toast.success("Digest preferences saved"); }
    catch { toast.error("Could not save preferences"); }
    finally { setDigestSaving(false); }
  };
  const [loading, setLoading] = useState(false);

  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState("");
  const [logoBust, setLogoBust] = useState(Date.now());
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = user?.role === "admin";

  // Email (Resend) config — admin only
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailKey, setEmailKey] = useState("");
  const [emailFrom, setEmailFrom] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTesting, setEmailTesting] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setAgency(user.agency || "");
      setPhone(user.phone || "");
      if (user.digest_prefs) setDigest(user.digest_prefs);
    }
    // setName/setAgency/setPhone are stable dispatch refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/agencies").then(({ data }) => {
      setAgencies(data || []);
      if (data?.length && !selectedAgency) setSelectedAgency(data[0].name);
    }).catch(() => {});
    api.get("/admin/email/config").then(({ data }) => {
      setEmailCfg(data);
      setEmailFrom(data?.from_email || "");
      setEmailTestTo(user?.email || "");
    }).catch(() => {});
    // selectedAgency is intentionally read-only here (one-shot default)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const saveEmailConfig = async () => {
    setEmailSaving(true);
    try {
      const body = { resend_from_email: emailFrom };
      if (emailKey && emailKey.trim()) body.resend_api_key = emailKey.trim();
      const { data } = await api.put("/admin/email/config", body);
      setEmailCfg(data);
      setEmailKey("");
      toast.success("Email settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setEmailSaving(false);
    }
  };

  const clearEmailKey = async () => {
    if (!window.confirm("Clear the stored Resend key? Emails will fall back to the RESEND_API_KEY env value (or fail if none is set).")) return;
    setEmailSaving(true);
    try {
      const { data } = await api.put("/admin/email/config", { resend_api_key: "" });
      setEmailCfg(data);
      setEmailKey("");
      toast.success("Key cleared");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    } finally {
      setEmailSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!emailTestTo) { toast.error("Enter a recipient"); return; }
    setEmailTesting(true);
    try {
      await api.post("/admin/email/test", { recipient: emailTestTo });
      toast.success(`Test email sent to ${emailTestTo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed — check backend logs");
    } finally {
      setEmailTesting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/auth/profile", { name, agency, phone });
      await refreshMe();
      toast.success("Profile updated");
    } catch { toast.error("Update failed"); }
    finally { setLoading(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedAgency) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/agencies/${encodeURIComponent(selectedAgency)}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Logo saved for ${selectedAgency}`);
      setLogoBust(Date.now());
      // refresh has-logo flags
      const { data } = await api.get("/agencies"); setAgencies(data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setLogoUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    if (!selectedAgency) return;
    if (!window.confirm(`Remove the logo for ${selectedAgency}?`)) return;
    try {
      await api.delete(`/admin/agencies/${encodeURIComponent(selectedAgency)}/logo`);
      toast.success("Logo removed");
      setLogoBust(Date.now());
      const { data } = await api.get("/agencies"); setAgencies(data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const currentAgency = agencies.find((a) => a.name === selectedAgency);
  const logoUrl = selectedAgency
    ? `${process.env.REACT_APP_BACKEND_URL}/api/agencies/${encodeURIComponent(selectedAgency)}/logo?t=${logoBust}`
    : null;
  const coverPreviewUrl = selectedAgency
    ? `${process.env.REACT_APP_BACKEND_URL}/api/agencies/${encodeURIComponent(selectedAgency)}/cover-preview.png?t=${logoBust}`
    : null;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="settings-page">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Preferences</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">Settings</h1>
        </div>

        {/* Profile */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6">
          <form onSubmit={save} className="space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-2">Profile</div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="settings-name-input"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Agency</Label>
              <Input value={agency} onChange={(e) => setAgency(e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="settings-agency-input"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="mt-1 h-10 rounded-sm" data-testid="settings-phone-input"/>
              <div className="text-[10px] text-[#6C757D] mt-1">Prefills the "Prepared by" contact card on every proforma cover sheet.</div>
            </div>
            <Button type="submit" disabled={loading} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="settings-save">
              <Save size={14} className="mr-2"/> {loading ? "Saving…" : "Save"}
            </Button>
          </form>
        </Card>

        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="digest-settings-card">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-1">Monday dealer digest</div>
          <p className="text-xs text-[#6C757D] mb-4">A Monday 9am ET email summarizing your dealers with missing install info, tasks due this week and upcoming appointments.</p>
          <div className="space-y-2 text-sm text-[#0B132B]">
            {[["enabled", "Send me the weekly digest"], ["flagged_dealers", "Include dealers with missing install info"], ["only_critical", "…only critical items (EIN, ACH, agreement, products)"], ["tasks", "Include tasks due this week"], ["appointments", "Include upcoming appointments"]].map(([k, l]) => (
              <label key={k} className={`flex items-center gap-2 ${k !== "enabled" && !digest.enabled ? "opacity-40" : ""}`}>
                <input type="checkbox" checked={!!digest[k]} disabled={digestSaving || (k !== "enabled" && !digest.enabled) || (k === "only_critical" && !digest.flagged_dealers)} onChange={(e) => saveDigest({ ...digest, [k]: e.target.checked })} className="accent-[#0B132B] h-4 w-4" data-testid={`digest-${k}`} />
                {l}
              </label>))}
          </div>
        </Card>

        {/* Agency branding (admin) */}
        {isAdmin && (
          <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="email-settings-card">
            <div className="flex items-center gap-2 mb-1">
              <Mail size={14} className="text-[#D4AF37]"/>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Email delivery</div>
              {emailCfg?.provider === "resend" && emailCfg?.has_key && (
                <span className="inline-flex items-center gap-1 ml-auto px-2 py-0.5 rounded-sm bg-green-50 text-green-700 text-[10px] uppercase tracking-[0.14em] font-bold" data-testid="email-provider-badge">
                  <CheckCircle2 size={10}/> Resend live
                </span>
              )}
              {(!emailCfg?.has_key) && (
                <span className="inline-flex items-center gap-1 ml-auto px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700 text-[10px] uppercase tracking-[0.14em] font-bold" data-testid="email-provider-badge">
                  <AlertCircle size={10}/> No key configured
                </span>
              )}
            </div>
            <p className="text-xs text-[#6C757D] mb-4 max-w-xl">
              Transactional emails (password resets, dealer sign-offs, install handoffs) route through your own Resend account. Get a key at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-[#0B132B] underline">resend.com</a> and paste it here. Use <span className="font-mono">onboarding@resend.dev</span> as the from-address for sandbox testing; add and verify your own domain in Resend before going live.
            </p>
            {emailCfg?.sandbox && (
              <div className="mb-4 p-3 rounded-sm border border-red-300 bg-red-50 text-xs text-red-800 flex gap-2 max-w-xl" data-testid="email-sandbox-warning">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold uppercase tracking-[0.14em] text-[10px] mb-1">Sandbox sender — dealers will NOT receive emails</div>
                  {emailCfg.sandbox_warning}{" "}
                  <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline font-semibold">Verify a domain →</a>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Resend API key</Label>
                <Input
                  type="password"
                  value={emailKey}
                  onChange={(e) => setEmailKey(e.target.value)}
                  placeholder={emailCfg?.has_key ? `Stored: ${emailCfg?.api_key_masked || "••••"}` : "re_..."}
                  className="mt-1 h-10 rounded-sm font-mono text-sm"
                  data-testid="email-key-input"
                />
                <div className="text-[10px] text-[#6C757D] mt-1">
                  {emailCfg?.source === "db" ? "Stored in database" : emailCfg?.source === "env" ? "Using env fallback" : "Not configured"}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">From email</Label>
                <Input
                  type="email"
                  value={emailFrom}
                  onChange={(e) => setEmailFrom(e.target.value)}
                  placeholder="onboarding@resend.dev"
                  className="mt-1 h-10 rounded-sm font-mono text-sm"
                  data-testid="email-from-input"
                />
                <div className="text-[10px] text-[#6C757D] mt-1">Sender name is <span className="font-semibold text-[#0B132B]">{emailCfg?.from_name || "Advantage Warranty Group"}</span> (set via EMAIL_FROM_NAME env)</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={saveEmailConfig} disabled={emailSaving} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="email-save-btn">
                <Save size={14} className="mr-2"/> {emailSaving ? "Saving…" : "Save settings"}
              </Button>
              {emailCfg?.source === "db" && (
                <Button variant="outline" onClick={clearEmailKey} disabled={emailSaving} className="h-10 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="email-clear-btn">
                  <Trash2 size={14} className="mr-2"/> Clear key
                </Button>
              )}
            </div>

            <div className="mt-6 pt-5 border-t border-[#E9ECEF]">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-2">Send test email</div>
              <div className="flex gap-2 flex-wrap">
                <Input
                  type="email"
                  value={emailTestTo}
                  onChange={(e) => setEmailTestTo(e.target.value)}
                  placeholder="you@example.com"
                  className="h-10 rounded-sm flex-1 min-w-[240px]"
                  data-testid="email-test-input"
                />
                <Button onClick={sendTestEmail} disabled={emailTesting || !emailCfg?.has_key} className="h-10 rounded-sm bg-[#D4AF37] hover:bg-[#B8952F] text-[#0B132B] font-bold" data-testid="email-test-btn">
                  <Send size={14} className="mr-2"/> {emailTesting ? "Sending…" : "Send test"}
                </Button>
              </div>
              <div className="text-[10px] text-[#6C757D] mt-2">
                Sandbox note: while using <span className="font-mono">onboarding@resend.dev</span>, Resend only delivers to your own account's owner email. Verify a domain in Resend to send to arbitrary recipients.
              </div>
            </div>
          </Card>
        )}

        {/* Agency branding (admin) */}
        {isAdmin && (
          <Card className="p-6 border-[#E9ECEF] rounded-sm" data-testid="agency-branding-card">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-1">Agency branding</div>
            <p className="text-xs text-[#6C757D] mb-4 max-w-lg">Upload each agency's logo — it co-brands the proforma page 1 header alongside the AWG mark on every PDF the agency's agents generate.</p>

            {agencies.length === 0 ? (
              <div className="text-sm text-[#6C757D] italic py-6 text-center border border-dashed border-[#E9ECEF] rounded-sm">
                No agencies yet — create agents with an agency name first.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  {agencies.map((a) => (
                    <button key={a.name} type="button" onClick={() => setSelectedAgency(a.name)}
                      className={`px-3 py-2 text-xs uppercase tracking-[0.14em] font-bold border rounded-sm transition-colors ${
                        selectedAgency === a.name ? "bg-[#0B132B] border-[#0B132B] text-white" : "bg-white border-[#E9ECEF] text-[#0B132B] hover:border-[#0B132B]/40"
                      }`}
                      data-testid={`agency-tab-${a.name}`}>
                      {a.name}{a.has_logo && <span className="ml-1.5 text-[#D4AF37]">●</span>}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-4 p-4 border border-[#E9ECEF] rounded-sm bg-[#F8F9FA]">
                  <div className="w-40 h-24 bg-white border border-[#E9ECEF] rounded-sm flex items-center justify-center overflow-hidden">
                    {currentAgency?.has_logo && logoUrl ? (
                      <img src={logoUrl} alt="agency logo" className="max-w-full max-h-full object-contain" data-testid="agency-logo-preview" />
                    ) : (
                      <ImageIcon size={26} className="text-[#6C757D]"/>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-[#0B132B]">{selectedAgency || "—"}</div>
                    <div className="text-[11px] text-[#6C757D] mt-0.5">.png / .jpg / .webp · max 2 MB · transparent PNG recommended</div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <label>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="hidden" data-testid="agency-logo-input"/>
                        <span className="inline-flex items-center gap-2 h-9 px-4 bg-[#0B132B] text-white rounded-sm cursor-pointer text-xs font-semibold uppercase tracking-[0.14em]">
                          <Upload size={12}/> {logoUploading ? "Uploading…" : (currentAgency?.has_logo ? "Replace" : "Upload logo")}
                        </span>
                      </label>
                      {currentAgency?.has_logo && (
                        <Button size="sm" variant="outline" onClick={removeLogo} className="h-9 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="agency-logo-remove">
                          <Trash2 size={12} className="mr-1.5"/> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cover-sheet live preview */}
                <div className="mt-4 border border-[#E9ECEF] rounded-sm overflow-hidden bg-white" data-testid="cover-preview-card">
                  <div className="px-4 py-2.5 flex items-center gap-2 border-b border-[#E9ECEF] bg-[#F8F9FA]">
                    <Eye size={13} className="text-[#0B132B]"/>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Cover-sheet preview</div>
                    <div className="text-[10px] text-[#6C757D] ml-auto">Updates each time you replace the logo</div>
                  </div>
                  {coverPreviewUrl ? (
                    <img
                      key={logoBust}
                      src={coverPreviewUrl}
                      alt="Cover sheet preview"
                      className="w-full h-auto max-h-[380px] object-contain bg-[#F8F9FA]"
                      data-testid="cover-preview-img"
                    />
                  ) : (
                    <div className="h-40 grid place-items-center text-xs text-[#6C757D] italic">Select an agency to preview.</div>
                  )}
                </div>
              </>
            )}
          </Card>
        )}
        {isAdmin && <DealerLookupsEditor />}
      </main>
    </div>
  );
}
